import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, Circle, useMap, useMapEvents } from 'react-leaflet';
import { createPortal } from 'react-dom';
import axios from '../api/axios';
import L from 'leaflet';
import AddEventModal from '../components/AddEventModal';
import type { NewEventPayload } from '../components/AddEventModal';
import AddUserModal from '../components/AddUserModal';
import ContactModal from '../components/ContactModal';
import LoginModal from '../components/LoginModal';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';
import TermsModal from '../components/TermsModal';
import { DateFilter } from '../components/DateFilter';
import { Sidebar } from '../components/Sidebar/index';
import PaginatedEventPopup from '../components/PaginatedEventPopup';
import SubmittedEventPopup from '../components/SubmittedEventPopup';
import RecurringTemplatePopup from '../components/RecurringTemplatePopup';
import type { RecurringTemplate } from '../components/RecurringTemplatePopup';
import HexbinLayer from '../components/HexbinLayer';
import type { SubmittedEvent } from '../components/SubmittedEventPopup';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { debounce } from 'lodash';
import { useUserRole } from '../hooks/useUserRole';
import {
  trackMarkerClick,
  trackEventSubmission,
  trackEventEdit,
  trackContactOpen,
  trackAddUser,
  trackPhraseSearch,
  trackNearMeClick,
  trackEventAdded,
  trackEventDeleted,
  trackSubmissionApproved,
  trackSubmissionRejected,
  trackSubmissionEdited,
  trackSubmissionDeleted,
  trackEventPopupOpen,
  trackDateFilter,
  trackTagFilter,
  trackSidebarOpen,
  trackAddEventModalOpen,
  trackPrivacyPolicyOpen,
  trackInstagramClick,
  trackDiscordClick,
} from '../utils/analytics';
import 'leaflet/dist/leaflet.css';
import type { Event } from '../types/Event';
import { CATEGORIES, CATEGORY_ORDER, getEventCategory, type CategoryId } from '../utils/eventCategory';
import { tierForZoom } from './eventsMapZoomTier';

// Reads ?event=<id> from the URL, flies the map to the event location, then
// Opens the same PaginatedEventPopup as a regular marker click, but triggered
// by the ?event=<id> deep-link query param. Uses an invisible zero-size marker
// so the popup has a Leaflet anchor without showing a visible pin.
// Lives inside <MapContainer> to access the map instance.
interface DeepLinkMarkerProps {
  event: Event;
  onClose: () => void;
  isLoggedIn: boolean;
  isModOrAdmin: boolean;
  onSave: (e: Event) => Promise<void>;
  onDelete: (id: string) => void;
  onOpenImagePreview: (imageUrl: string, title: string) => void;
  onReportCountChange: (eventId: string, count: number) => void;
}
function DeepLinkMarker({
  event, onClose, isLoggedIn, isModOrAdmin, onSave, onDelete, onOpenImagePreview, onReportCountChange,
}: DeepLinkMarkerProps) {
  const map = useMap();
  const markerRef = useRef<L.Marker | null>(null);
  const didFlyRef = useRef(false);

  useEffect(() => {
    // Open the popup only once flyTo has actually finished moving the map.
    // A fixed setTimeout can fire while flyTo is still animating (its real
    // duration isn't guaranteed to match the requested hint for long
    // flights), and CenterPopupOnOpen's corrective panBy gets silently
    // overwritten by the still-running flyTo animation frame, leaving the
    // popup stuck off-center. The listener is (re)registered on every effect
    // run (not just the first) so it survives React StrictMode's
    // mount/cleanup/mount dev-mode double-invoke; only the flyTo call itself
    // is guarded to run once.
    const handleMoveEnd = () => {
      map.off('moveend', handleMoveEnd);
      markerRef.current?.openPopup();
    };
    map.on('moveend', handleMoveEnd);

    if (!didFlyRef.current) {
      didFlyRef.current = true;
      map.flyTo([event.lat, event.lng], 16, { animate: true, duration: 1.2 });
    }

    return () => {
      map.off('moveend', handleMoveEnd);
    };
  }, [event, map]);

  const icon = useMemo(() => L.divIcon({ className: '', iconSize: L.point(0, 0) }), []);

  return (
    <Marker
      ref={markerRef}
      position={[event.lat, event.lng]}
      icon={icon}
      eventHandlers={{ popupclose: onClose }}
    >
      <Popup closeButton={true} maxWidth={560} autoPan={false}>
        <PaginatedEventPopup
          events={[event]}
          isLoggedIn={isLoggedIn}
          isModOrAdmin={isModOrAdmin}
          onSave={onSave}
          onDelete={onDelete}
          onOpenImagePreview={onOpenImagePreview}
          onReportCountChange={onReportCountChange}
        />
      </Popup>
    </Marker>
  );
}

// Fix for default marker icons in production
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function CenterPopupOnOpen() {
  const map = useMap();

  useEffect(() => {
    const handlePopupOpen = (event: L.PopupEvent) => {
      // Hex-cell popups re-fetch/re-render their layer on every map move
      // (moveend), so panning the map to center them here would trigger
      // that recompute mid-open and make the popup appear to flicker/close.
      const className = event.popup.options.className;
      if (className && className.includes('hexbin-popup')) {
        return;
      }

      window.requestAnimationFrame(() => {
        const popupElement = event.popup.getElement();
        const mapElement = map.getContainer();

        if (!popupElement || !mapElement) {
          return;
        }

        const popupRect = popupElement.getBoundingClientRect();
        const mapRect = mapElement.getBoundingClientRect();
        const horizontalOffset = popupRect.left + popupRect.width / 2 - (mapRect.left + mapRect.width / 2);
        const verticalOffset = popupRect.top + popupRect.height / 2 - (mapRect.top + mapRect.height / 2);

        // Only bother nudging the map when the popup is meaningfully
        // off-center (not just a couple of pixels) — small corrections
        // aren't worth the moveend they trigger downstream (bbox refetch,
        // hex recompute, etc.), which is what was causing popups to
        // flicker open/closed after opening.
        const offCenterThreshold = 40;
        if (
          Math.abs(horizontalOffset) < offCenterThreshold &&
          Math.abs(verticalOffset) < offCenterThreshold
        ) {
          return;
        }

        map.panBy([horizontalOffset, verticalOffset], {
          animate: true,
          duration: 0.35,
        });
      });
    };

    map.on('popupopen', handlePopupOpen);

    return () => {
      map.off('popupopen', handlePopupOpen);
    };
  }, [map]);

  return null;
}

// Lowers filters-top-bar z-index when a popup is open so the popup appears
// above it. The leaflet-map-pane has a CSS transform which creates an isolated
// stacking context (z-index: auto in root), so external elements with any
// positive z-index always paint above the popup. Lowering filters to 0 lets
// the map-pane (later in DOM) paint on top.
function PopupZIndexManager() {
  useMapEvents({
    popupopen: () => {
      // These elements are before map-pane in DOM — z-index: 0 is enough
      // to let map-pane (later in DOM, same stacking level) paint on top.
      ['filters-top-bar', 'floating-legend-and-social-links', 'floating-menu-button'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.style.zIndex = '0';
      });
      // .leaflet-control-container is position:static so z-index has no effect
      // on it. The actual stacking context is created by .leaflet-top
      // (position:absolute, z-index:1000) which sits AFTER map-pane in DOM.
      // Negative z-index is required to push it below map-pane (auto level).
      // Mobile only — on desktop zoom controls rarely overlap with popups.
      if (window.innerWidth < 768) {
        document.querySelectorAll('.leaflet-top').forEach(el => {
          (el as HTMLElement).style.zIndex = '-1';
        });
      }
    },
    popupclose: () => {
      const restore: Record<string, string> = {
        'filters-top-bar': '699',
        'floating-legend-and-social-links': '2147483646',
        'floating-menu-button': '2147483647',
      };
      Object.entries(restore).forEach(([id, z]) => {
        const el = document.getElementById(id);
        if (el) el.style.zIndex = z;
      });
      document.querySelectorAll('.leaflet-top').forEach(el => {
        (el as HTMLElement).style.zIndex = '';
      });
    },
  });
  return null;
}

// Imperatively pans/zooms the map to fit a given center + radius (metres).
function FitToRadius({ center, radius }: { center: [number, number]; radius: number }) {
  const map = useMap();

  useEffect(() => {
    const bounds = L.latLng(center[0], center[1]).toBounds(radius * 2);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, center, radius]);

  return null;
}

// When a phrase search returns results, pans the map to fit all of them so
// the user can see the markers regardless of where the map was before.
function FlyToSearchResults({ events }: { events: Event[] | null }) {
  const map = useMap();
  const prevCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (!events || events.length === 0) {
      prevCountRef.current = null;
      return;
    }
    // Only fly when the result set actually changes (new search), not on
    // every re-render with the same results.
    if (prevCountRef.current === events.length) return;
    prevCountRef.current = events.length;

    // A marker's popup can already be open by the time this effect runs (e.g. a user
    // clicking the marker the instant it appears, right after the search resolves, before
    // this effect has had a chance to run). flyTo/flyToBounds here can change the zoom
    // level, which makes the marker-cluster layer regroup markers for the new zoom and
    // silently destroys any currently-open popup as a side effect. Skip the fly in that
    // case rather than yanking the map out from under something the user just opened.
    if (map.getContainer().querySelector('.leaflet-popup')) {
      return;
    }

    if (events.length === 1) {
      map.flyTo([events[0].lat, events[0].lng], 14, { animate: true, duration: 1 });
    } else {
      const bounds = L.latLngBounds(events.map(e => [e.lat, e.lng] as [number, number]));
      map.flyToBounds(bounds, { padding: [60, 60], animate: true, duration: 1 });
    }
  }, [map, events]);

  return null;
}

// Reports the current map zoom level up to the parent so it can pick which
// layers (hexagons vs markers) to render — must live inside <MapContainer>
// to access the map instance via react-leaflet hooks.
function ZoomTierWatcher({ onZoomChange }: { onZoomChange: (zoom: number) => void }) {
  const map = useMap();

  useMapEvents({
    zoomend: () => onZoomChange(map.getZoom()),
  });

  useEffect(() => {
    onZoomChange(map.getZoom());
  }, [map, onZoomChange]);

  return null;
}

// Reports the current viewport as a "minLng,minLat,maxLng,maxLat" bbox
// string, debounced, so the parent can scope /events (and /events/aggregate)
// requests to what's actually visible instead of ever fetching the full
// table — this is what lets the map stay responsive regardless of how many
// events exist in the database (thousands or millions).
function MapBoundsWatcher({ onBoundsChange }: { onBoundsChange: (bbox: string) => void }) {
  const map = useMap();
  // True while a popup is open. Opening a popup runs a programmatic
  // centering pan (see CenterPopupOnOpen), which fires `moveend`. If we
  // reported that as a new bbox, the parent would refetch events and
  // rebuild MarkerClusterGroup's layers — destroying the just-opened popup
  // and making it visibly flicker. So we pause bbox reporting while a popup
  // is open and do a single catch-up report once it closes.
  const popupOpenRef = useRef(false);
  // Holds the current debounced reporter. Created in an effect (rather than
  // useMemo) so the ref read of `popupOpenRef` happens only when the
  // debounced function is actually invoked (event handler/effect), never
  // during render.
  const debouncedReportRef = useRef<ReturnType<typeof debounce> | null>(null);

  const report = useCallback(() => {
    debouncedReportRef.current?.();
  }, []);

  useMapEvents({
    moveend: () => report(),
    zoomend: () => report(),
    popupopen: () => {
      popupOpenRef.current = true;
    },
    popupclose: () => {
      popupOpenRef.current = false;
      // Catch up on any viewport change that happened while the popup was
      // open (e.g. the user dragged the map around before closing it).
      report();
    },
  });

  useEffect(() => {
    const debounced = debounce(() => {
      if (popupOpenRef.current) {
        return;
      }
      const bounds = map.getBounds();
      onBoundsChange(
        [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()].join(',')
      );
    }, 300);
    debouncedReportRef.current = debounced;
    debounced();
    return () => {
      debounced.cancel();
      debouncedReportRef.current = null;
    };
  }, [map, onBoundsChange]);

  return null;
}

export default function EventsMap() {
  const [imagePreview, setImagePreview] = useState<{ imageUrl: string; title: string } | null>(null);
  const imagePreviewOpenedAtRef = useRef(0);
  const addModalOpenedAtRef = useRef(0);
  const contactModalOpenedAtRef = useRef(0);
  const privacyPolicyModalOpenedAtRef = useRef(0);
  const termsModalOpenedAtRef = useRef(0);
  // AbortController for the in-flight /events request. When a new fetch
  // starts (new bbox, zoom, filters) we abort the previous one so that a
  // stale slow response can never overwrite a fresher fast one, and so we
  // don't waste DB connections on requests whose results will be discarded.
  const fetchAbortControllerRef = useRef<AbortController | null>(null);
  // Last "signature" of fetched events (id + reportCount), used by
  // fetchEvents to skip setEvents() when a background refresh returns the
  // same data — see the comment there for why that matters.
  const eventsSignatureRef = useRef<string>('');
  const [events, setEvents] = useState<Event[]>([]);
  const [isEventsLoading, setIsEventsLoading] = useState(true);
  // Only used to gate the initial full-page spinner below — subsequent
  // background refetches (triggered by panning/zooming the map) must NOT
  // unmount <MapContainer>, otherwise it remounts at its default
  // center/zoom and the user's current view is lost.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  // Results from full-text phrase search (/events/search). When non-null,
  // these replace the viewport-scoped events on the map.
  const [searchResults, setSearchResults] = useState<Event[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isPrivacyPolicyModalOpen, setIsPrivacyPolicyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  // Current map viewport as "minLng,minLat,maxLng,maxLat", reported by
  // MapBoundsWatcher. Scopes /events requests to what's visible so the map
  // never has to fetch the entire table.
  //
  // bbox is stored in BOTH state and a ref:
  // - state drives the useEffect below (triggers refetch on pan/zoom)
  // - ref is read synchronously inside fetchEvents so bbox isn't a dep of
  //   useCallback — without the ref, every pan/zoom recreated fetchEvents,
  //   which triggered the filter useEffect a second time, causing a
  //   duplicate unscoped fetch on initial load.
  const [bbox, setBbox] = useState<string | null>(null);
  const bboxRef = useRef<string | null>(null);
  const handleBoundsChange = useCallback((nextBbox: string) => {
    bboxRef.current = nextBbox;
    setBbox(nextBbox);
  }, []);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  const [isSubmissionsView, setIsSubmissionsView] = useState(false);
  const [submissions, setSubmissions] = useState<SubmittedEvent[]>([]);
  const [isRecurringTemplatesView, setIsRecurringTemplatesView] = useState(false);
  const [recurringTemplates, setRecurringTemplates] = useState<RecurringTemplate[]>([]);
  // map of eventId → report count (populated lazily as popups open)
  const [reportCounts, setReportCounts] = useState<Record<string, number>>({});

  // Same effective date range used by fetchEvents (below), computed once so
  // HexbinLayer's /events/aggregate calls stay in sync with the /events
  // calls — otherwise the "exact" hex counts could reflect a different date
  // window than what's actually shown as markers.
  const effectiveDateRange = useMemo(() => {
    if (showArchived) {
      const now = new Date();
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(now.getMonth() - 1);
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        startDate: oneMonthAgo.toISOString().split('T')[0],
        endDate: yesterday.toISOString().split('T')[0],
      };
    }
    return {
      startDate: startDate || new Date().toISOString().split('T')[0],
      endDate: endDate || null,
    };
  }, [showArchived, startDate, endDate]);

  // 'markers' always shows every event as an individual/clustered marker.
  // 'hexagons' is a density-aware hybrid: cells with more than
  // HEX_DENSITY_THRESHOLD events are drawn as a hexagon, the rest still show
  // as individual markers — the switch is driven by event count, not zoom.
  const [mapZoom, setMapZoom] = useState(7);
  const zoomTier = useMemo(() => tierForZoom(mapZoom), [mapZoom]);
  const handleZoomChange = useCallback((zoom: number) => setMapZoom(zoom), []);
  // Sparse events reported by HexbinLayer at the 'near' zoom tier — cells
  // too small to justify a hexagon fall back to individual markers.
  const [sparseEvents, setSparseEvents] = useState<Event[]>([]);
  const handleSparseEventsChange = useCallback((events: Event[]) => {
    setSparseEvents(events);
  }, []);
  // PostGIS radius search result (near-me), or null when inactive.
  const [nearby, setNearby] = useState<{ center: [number, number]; radius: number; ids: Set<string> } | null>(null);
  const [isLocating, setIsLocating] = useState(false);

  // Deep-link: event loaded via ?event=<id> query param.
  const [deepLinkEvent, setDeepLinkEvent] = useState<Event | null>(null);
  const handleDeepLinkHandled = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('event');
    window.history.replaceState(null, '', url.toString());
    setDeepLinkEvent(null);
  }, []);

  // Show the login modal instead of redirecting when the session expires
  // (e.g. token missing/expired after a computer restart).
  useEffect(() => {
    const handleSessionExpired = () => {
      setIsLoggedIn(false);
      setIsLoginModalOpen(true);
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  // On mount: read ?event=<id> and fetch that event so DeepLinkHandler can fly to it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const eventId = params.get('event');
    if (!eventId) return;
    axios.get(`/events/${eventId}`).then(res => {
      setDeepLinkEvent(res.data);
    }).catch(() => {
      // Event not found or request failed — silently ignore.
    });
  }, []);

  const handleReportCountChange = useCallback((eventId: string, count: number) => {
    // Bail out when the count hasn't actually changed. EventPopup calls
    // this on every mount (it re-fetches its report count), so without
    // this guard every popup open creates a new `reportCounts` object,
    // which cascades into MarkerClusterGroup rebuilding all its marker
    // layers — destroying the just-opened popup and re-triggering this
    // same fetch, in an open/close loop.
    //
    // Treat a missing entry as 0: the reports endpoint is public, so every
    // popup open reports a count (0 for the vast majority of events). A
    // plain `prev[eventId] === count` check fails here because
    // `undefined === 0` is false, so the first open of every marker would
    // still churn `reportCounts` and make the popup flicker.
    setReportCounts(prev => ((prev[eventId] ?? 0) === count ? prev : { ...prev, [eventId]: count }));
  }, []);

  const openImagePreview = useCallback((imageUrl: string, title: string) => {
    imagePreviewOpenedAtRef.current = Date.now();
    setImagePreview({ imageUrl, title });
  }, []);

  const openContactModal = useCallback(() => {
    contactModalOpenedAtRef.current = Date.now();
    setIsContactModalOpen(true);
    trackContactOpen();
  }, []);

  const openPrivacyPolicyModal = useCallback(() => {
    privacyPolicyModalOpenedAtRef.current = Date.now();
    setIsPrivacyPolicyModalOpen(true);
    trackPrivacyPolicyOpen();
  }, []);

  const openTermsModal = useCallback(() => {
    termsModalOpenedAtRef.current = Date.now();
    setIsTermsModalOpen(true);
  }, []);

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarOpen(prev => {
      if (!prev) trackSidebarOpen();
      return !prev;
    });
  }, []);

  const handleSidebarClose = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');

    const handleViewportChange = (event: MediaQueryListEvent) => {
      if (!event.matches) setIsSidebarOpen(false);
    };

    mediaQuery.addEventListener('change', handleViewportChange);

    const checkAuth = () => {
      setIsLoggedIn(!!localStorage.getItem('token'));
    };

    const handleOpenAddEventModal = () => {
      addModalOpenedAtRef.current = Date.now();
      setIsAddModalOpen(true);
      trackAddEventModalOpen();
    };

    const handleOpenAddUserModal = () => {
      setIsAddUserModalOpen(true);
      trackAddUser();
    };

    const handleOpenContactModal = () => {
      openContactModal();
    };

    const handleOpenPrivacyPolicyModal = () => {
      openPrivacyPolicyModal();
    };

    const handleOpenTermsModal = () => {
      openTermsModal();
    };

    const handleOpenSubmissionsView = () => {
      setIsSubmissionsView(true);
      setIsRecurringTemplatesView(false);
    };

    const handleCloseSubmissionsView = () => {
      setIsSubmissionsView(false);
    };

    const handleOpenRecurringTemplatesView = () => {
      setIsRecurringTemplatesView(true);
      setIsSubmissionsView(false);
    };

    const handleCloseRecurringTemplatesView = () => {
      setIsRecurringTemplatesView(false);
    };

    // Sprawdź przy montowaniu i nasłuchuj na zmiany w localStorage
    checkAuth();
    window.addEventListener('storage', checkAuth);
    window.addEventListener('openAddEventModal', handleOpenAddEventModal);
    window.addEventListener('openAddUserModal', handleOpenAddUserModal);
    window.addEventListener('openContactModal', handleOpenContactModal);
    window.addEventListener('openPrivacyPolicyModal', handleOpenPrivacyPolicyModal);
    window.addEventListener('openTermsModal', handleOpenTermsModal);
    window.addEventListener('openSubmissionsView', handleOpenSubmissionsView);
    window.addEventListener('closeSubmissionsView', handleCloseSubmissionsView);
    window.addEventListener('openRecurringTemplatesView', handleOpenRecurringTemplatesView);
    window.addEventListener('closeRecurringTemplatesView', handleCloseRecurringTemplatesView);

    return () => {
      mediaQuery.removeEventListener('change', handleViewportChange);
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('openAddEventModal', handleOpenAddEventModal);
      window.removeEventListener('openAddUserModal', handleOpenAddUserModal);
      window.removeEventListener('openContactModal', handleOpenContactModal);
      window.removeEventListener('openPrivacyPolicyModal', handleOpenPrivacyPolicyModal);
      window.removeEventListener('openTermsModal', handleOpenTermsModal);
      window.removeEventListener('openSubmissionsView', handleOpenSubmissionsView);
      window.removeEventListener('closeSubmissionsView', handleCloseSubmissionsView);
      window.removeEventListener('openRecurringTemplatesView', handleOpenRecurringTemplatesView);
      window.removeEventListener('closeRecurringTemplatesView', handleCloseRecurringTemplatesView);
    };
  }, [openContactModal, openPrivacyPolicyModal, openTermsModal]);
  const { userRole, isLoading: isRoleLoading } = useUserRole();
  const isModOrAdmin = userRole === 'ADMIN' || userRole === 'MODERATOR';

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await axios.get('/submitted-events');
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  }, []);

  useEffect(() => {
    if (isSubmissionsView && isModOrAdmin) {
      fetchSubmissions();
    }
  }, [isSubmissionsView, fetchSubmissions, isModOrAdmin]);

  const fetchRecurringTemplates = useCallback(async () => {
    try {
      const response = await axios.get('/events/recurring/templates');
      setRecurringTemplates(response.data);
    } catch (error) {
      console.error('Error fetching recurring templates:', error);
    }
  }, []);

  useEffect(() => {
    if (isRecurringTemplatesView && isModOrAdmin) {
      fetchRecurringTemplates();
    }
  }, [isRecurringTemplatesView, fetchRecurringTemplates, isModOrAdmin]);

  const handleSaveRecurringTemplate = useCallback((updated: RecurringTemplate) => {
    setRecurringTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  }, []);

  const handleDeleteRecurringTemplate = useCallback((id: string) => {
    setRecurringTemplates((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleApproveSubmission = useCallback(async (id: string, recurrenceCron?: string) => {
    try {
      await axios.post(`/submitted-events/${id}/approve`, recurrenceCron ? { recurrenceCron } : {});
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status: 'APPROVED' as const } : s));
      trackSubmissionApproved();
    } catch (error) {
      console.error('Error approving submission:', error);
      alert('Nie udało się zatwierdzić zgłoszenia');
    }
  }, []);

  const handleRejectSubmission = useCallback(async (id: string) => {
    try {
      await axios.post(`/submitted-events/${id}/reject`, {});
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status: 'REJECTED' as const } : s));
      trackSubmissionRejected();
    } catch (error) {
      console.error('Error rejecting submission:', error);
      alert('Nie udało się odrzucić zgłoszenia');
    }
  }, []);

  const handleEditSubmission = useCallback(async (id: string, data: Partial<import('../components/SubmittedEventPopup').SubmittedEvent>) => {
    try {
      const response = await axios.patch(`/submitted-events/${id}`, data);
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, ...response.data } : s));
      trackSubmissionEdited();
    } catch (error) {
      console.error('Error updating submission:', error);
      alert('Nie udało się zapisać zmian');
    }
  }, []);

  const handleDeleteSubmission = useCallback(async (id: string) => {
    try {
      await axios.delete(`/submitted-events/${id}`);
      setSubmissions(prev => prev.filter(s => s.id !== id));
      trackSubmissionDeleted();
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('Nie udało się usunąć zgłoszenia');
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setIsLoggedIn(false);
    window.location.reload();
  }, []);

  const handleClearNearby = useCallback(() => {
    setNearby(null);
  }, []);

  const handleNearMe = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Twoja przeglądarka nie obsługuje geolokalizacji');
      return;
    }
    trackNearMeClick();
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const radius = 10000; // 10 km
        try {
          const params = new URLSearchParams({
            lat: String(latitude),
            lng: String(longitude),
            radius: String(radius),
          });
          const response = await axios.get(`/events/nearby?${params.toString()}`);
          const ids = new Set<string>(response.data.map((e: { id: string }) => e.id));
          setNearby({ center: [latitude, longitude], radius, ids });
        } catch (error) {
          console.error('Error fetching nearby events:', error);
          const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
          alert(message === 'Coordinates are outside the supported region (Poland)'
            ? 'Wyszukiwanie w pobliżu działa obecnie tylko w Polsce'
            : 'Nie udało się pobrać wydarzeń w pobliżu');
        } finally {
          setIsLocating(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsLocating(false);
        alert('Nie udało się ustalić Twojej lokalizacji');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const fetchEvents = useCallback(async (start: string | null = null, end: string | null = null, tags: string[] = []) => {
    // Abort any in-flight request before starting a new one. This prevents
    // a slow previous response from overwriting a faster newer one (race
    // condition), and releases the DB connection immediately rather than
    // waiting for a result we're going to throw away anyway.
    fetchAbortControllerRef.current?.abort();
    const controller = new AbortController();
    fetchAbortControllerRef.current = controller;

    try {
      // Only toggle the loading flag for the very first request. Every pan/
      // zoom re-triggers this (via the bbox dependency) purely to keep the
      // viewport-scoped dataset fresh in the background — flipping
      // isEventsLoading for those causes an extra render on every single
      // map movement, which is exactly the kind of churn that can tear
      // down/rebuild marker cluster layers and close an open popup.
      if (!hasLoadedOnce) {
        setIsEventsLoading(true);
      }
      const now = new Date();
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(now.getMonth() - 1);

      const params = new URLSearchParams();
      
      if (showArchived) {
        // For archived events: from one month ago to yesterday
        params.append('startDate', oneMonthAgo.toISOString().split('T')[0]);
        params.append('endDate', new Date(now.setDate(now.getDate() - 1)).toISOString().split('T')[0]);
      } else {
        // For current events: from today onwards
        params.append('startDate', start || new Date().toISOString().split('T')[0]);
        if (end) params.append('endDate', end);
      }

      // Scope the request to the current viewport and cap the page size —
      // without this, the endpoint would serialize every matching event in
      // the database on every fetch, which doesn't scale past a few
      // thousand rows.
      if (bboxRef.current) {
        params.append('bbox', bboxRef.current);
      }
      if (tags.length > 0) {
        params.append('tags', tags.join(','));
      }
      params.append('limit', '2000');
      params.append('slim', 'true');
      const url = `/events?${params.toString()}`;

      const response = await axios.get(url, { signal: controller.signal });
      const nextEvents: Event[] = response.data;

      // Skip the state update entirely when the fetched data is the same
      // as what's already shown (extremely common for background refreshes
      // triggered by a small/centering pan rather than a real navigation).
      // Any `setEvents` call re-renders the whole tree, which forces
      // MarkerClusterGroup to rebuild its layers and silently closes
      // whatever popup the user just opened — so it's worth avoiding when
      // nothing actually changed.
      const nextSignature = nextEvents.map(e => `${e.id}:${e.reportCount ?? 0}`).sort().join('|');
      if (nextSignature !== eventsSignatureRef.current) {
        eventsSignatureRef.current = nextSignature;
        setEvents(nextEvents);
      }

      // availableTags are now populated via /events/tags/autocomplete
      // (debounced on tagInput change) rather than being derived from the
      // viewport-scoped events, so there's nothing to collect here.
    } catch (error) {
      // An AbortError means we deliberately cancelled the request because a
      // newer one superseded it — not an actual error, so don't clear events.
      if (error instanceof Error && error.name === 'CanceledError') {
        return;
      }
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      // Only update loading state if this controller is still the active one
      // (i.e. it wasn't superseded by a subsequent fetch that already ran its
      // own finally block).
      if (fetchAbortControllerRef.current === controller) {
        setIsEventsLoading(false);
        setHasLoadedOnce(true);
      }
    }
  }, [showArchived, hasLoadedOnce]);

  const handleApproveAndRefresh = useCallback(async (id: string, recurrenceCron?: string) => {
    await handleApproveSubmission(id, recurrenceCron);
    await fetchEvents(startDate, endDate);
  }, [handleApproveSubmission, fetchEvents, startDate, endDate]);

  const handleDateFilterChange = useCallback((newStartDate: string | null, newEndDate: string | null) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    fetchEvents(newStartDate, newEndDate, selectedTags);
    trackDateFilter(newStartDate, newEndDate);
  }, [fetchEvents, selectedTags]);

  const handleClearFilters = useCallback(() => {
    setSelectedTags([]);
    setTagInput('');
    setSearchResults(null);
    setAvailableTags([]);
  }, []);

  // Debounced autocomplete + phrase-search effect.
  // - tagInput ≥ 2 chars → fetch autocomplete suggestions
  // - tagInput ≥ 3 chars → fetch full-text search results (shown on map)
  useEffect(() => {
    if (tagInput.length < 2) {
      setAvailableTags([]);
      setSearchResults(null);
      setIsSearching(false);
      return;
    }

    const autocompleteTimer = setTimeout(async () => {
      try {
        const response = await axios.get(`/events/tags/autocomplete?q=${encodeURIComponent(tagInput)}`);
        setAvailableTags(response.data);
      } catch {
        // ignore — non-critical
      }
    }, 300);

    let phraseTimer: ReturnType<typeof setTimeout> | undefined;
    if (tagInput.length >= 3) {
      setIsSearching(true);
      phraseTimer = setTimeout(async () => {
        try {
          const params = new URLSearchParams({ phrase: tagInput, limit: '500' });
          if (effectiveDateRange.startDate) params.append('startDate', effectiveDateRange.startDate);
          if (effectiveDateRange.endDate) params.append('endDate', effectiveDateRange.endDate);
          const response = await axios.get(`/events/search?${params.toString()}`);
          setSearchResults(response.data);
          trackPhraseSearch(tagInput, response.data.length);
        } catch {
          setSearchResults(null);
        } finally {
          setIsSearching(false);
        }
      }, 500);
    } else {
      setSearchResults(null);
    }

    return () => {
      clearTimeout(autocompleteTimer);
      if (phraseTimer !== undefined) clearTimeout(phraseTimer);
    };
  }, [tagInput, effectiveDateRange]);

  const handleTagInputSubmit = useCallback(() => {
    const newTag = tagInput.trim();
    if (!newTag || selectedTags.includes(newTag)) {
      return;
    }

    const next = [...selectedTags, newTag];
    setSelectedTags(next);
    setTagInput('');
    trackTagFilter(newTag);
    fetchEvents(startDate, endDate, next);
  }, [tagInput, selectedTags, fetchEvents, startDate, endDate]);

  const handleDelete = useCallback(async (eventId: string, scope: 'instance' | 'series' = 'instance') => {
    try {
      await axios.delete(`/events/${eventId}`, { params: { scope } });
      if (scope === 'series') {
        // Multiple rows (template + occurrences) were removed server-side — refetch to sync.
        fetchEvents(startDate, endDate, selectedTags);
      } else {
        setEvents(events => events.filter(e => e.id !== eventId));
        setSearchResults(results => results ? results.filter(e => e.id !== eventId) : results);
      }
      trackEventDeleted(eventId);
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Nie udało się usunąć wydarzenia');
    }
  }, [fetchEvents, startDate, endDate, selectedTags]);

  const handleEditSubmit = useCallback(async (updatedEvent: Event) => {
    try {
      await axios.put(`/events/${updatedEvent.id}`, updatedEvent);
      setEvents(events => events.map(e => 
        e.id === updatedEvent.id ? updatedEvent : e
      ));
      setSearchResults(results => results ? results.map(e => e.id === updatedEvent.id ? updatedEvent : e) : results);
      trackEventEdit();
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Nie udało się zaktualizować wydarzenia');
    }
  }, []);

  const handleAddEvent = useCallback(async (newEvent: NewEventPayload) => {
    try {
      const response = await axios.post('/events', newEvent);
      setEvents(prev => [...prev, response.data]);
      setIsAddModalOpen(false);
      trackEventSubmission(isLoggedIn ? 'mod_admin' : 'user');
      trackEventAdded(isLoggedIn ? 'mod_admin' : 'user');
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Nie udało się utworzyć wydarzenia');
    }
  }, [isLoggedIn]);

  // Filter events based on selected tags.
  // When a phrase search is active, searchResults replace the viewport events.
  const filteredEvents = useMemo(
    () => (searchResults ?? events).filter(event =>
      (selectedTags.length === 0 ||
        event.tags?.some(tag =>
          selectedTags.some(selectedTag =>
            selectedTag.toLowerCase() === tag.toLowerCase()
          )
        )) &&
      (!nearby || nearby.ids.has(event.id))
    ),
    [events, searchResults, selectedTags, nearby]
  );

  // What the hex layer bins vs. what renders as individual markers is
  // driven purely by `zoomTier` (see `tierForZoom`):
  // - far/medium: every event is binned into hexagons, no markers at all.
  // - near: hexagons stay for dense cells; sparse cells are reported back
  //   via `handleSparseEventsChange` and rendered as markers below.
  // - close: no hex layer, every event is a marker.
  // Exception: when a phrase search is active and returns ≤500 results,
  // skip hexbins entirely and show only markers regardless of zoom tier.
  const isSparsePhraseSearch = searchResults !== null && searchResults.length <= 500;
  const hexEvents = (zoomTier === 'close' || isSparsePhraseSearch) ? [] : filteredEvents;
  const markerEvents = useMemo(() => {
    if (zoomTier === 'close' || isSparsePhraseSearch) return filteredEvents;
    if (zoomTier === 'near') return sparseEvents;
    return [];
  }, [zoomTier, filteredEvents, sparseEvents, isSparsePhraseSearch]);

  // Confirmed events with at least one report — surfaced in "Tryb zgłoszeń"
  // alongside pending submissions so moderators can review them in one place.
  const reportedEvents = useMemo(
    () => events.filter(event => (reportCounts[event.id] ?? event.reportCount ?? 0) > 0),
    [events, reportCounts]
  );

  // Group events by location and sort each group by startTime.
  const groupedEvents = useMemo(() => {
    return Object.entries(
      markerEvents.reduce<Record<string, Event[]>>((acc, event) => {
        const key = `${event.lat},${event.lng}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(event);
        return acc;
      }, {})
    ).map(([locationKey, group]) => {
      const sorted = [...group].sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
      return { locationKey, sorted };
    });
  }, [markerEvents]);

  const markerEventHandlers = useMemo(
    () =>
      groupedEvents.map(({ locationKey, sorted }) => ({
        locationKey,
        handlers: {
          click: () => {
            trackMarkerClick();
            trackEventPopupOpen(sorted[0].id, sorted[0].title);
          },
        },
      })),
    [groupedEvents]
  );

  // Stable function reference for MarkerClusterGroup's `iconCreateFunction`.
  // Passing a fresh arrow function on every render gives the underlying
  // leaflet.markercluster instance a "new" prop on every unrelated
  // re-render of EventsMap, which appears to make it reinitialize its
  // layers (and silently close whatever popup is currently open).
  const clusterIconCreateFunction = useCallback((cluster: { getChildCount: () => number }) => {
    const count = cluster.getChildCount();
    return L.divIcon({
      html: `<div style="width:40px;height:40px;border-radius:50%;background:rgba(37,99,235,0.85);border:3px solid #ffffff;box-shadow:0 2px 8px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;color:#ffffff;font-weight:700;font-size:14px;font-family:'Helvetica Neue',Arial,sans-serif;">${count}</div>`,
      className: '',
      iconSize: L.point(40, 40),
      iconAnchor: L.point(20, 20),
    });
  }, []);

  // One L.divIcon instance per category, precomputed once (CATEGORY_ORDER is
  // a fixed, known list) so we never rebuild them on render and never need
  // to mutate a cache during render (which would violate rules-of-hooks
  // around refs/immutability).
  const categoryIcons = useMemo(() => {
    const icons = new Map<CategoryId, L.DivIcon>();
    for (const category of CATEGORY_ORDER) {
      const info = CATEGORIES[category];
      icons.set(category, L.divIcon({
        html: `<div style="width:34px;height:34px;border-radius:50%;background:${info.color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-size:16px;">${info.icon}</div>`,
        className: '',
        iconSize: L.point(34, 34),
        iconAnchor: L.point(17, 17),
      }));
    }
    return icons;
  }, []);
  const getCategoryIcon = useCallback((category: CategoryId) => {
    return categoryIcons.get(category)!;
  }, [categoryIcons]);

  const submissionMarkerIcon = useMemo(() => L.divIcon({
    html: `<div style="width:32px;height:32px;border-radius:50%;background:rgba(234,88,12,0.9);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5">
        <circle cx="12" cy="12" r="9"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    </div>`,
    className: '',
    iconSize: L.point(32, 32),
    iconAnchor: L.point(16, 16),
  }), []);

  const reportedMarkerIcon = useMemo(() => L.divIcon({
    html: `<div style="width:36px;height:36px;border-radius:50%;background:rgba(217,119,6,0.93);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-weight:900;font-size:22px;color:#fff;font-family:'Helvetica Neue',Arial,sans-serif;line-height:1;">?</div>`,
    className: '',
    iconSize: L.point(36, 36),
    iconAnchor: L.point(18, 18),
  }), []);

  const paidSubmissionMarkerIcon = useMemo(() => L.divIcon({
    html: `<div style="width:36px;height:36px;border-radius:50%;background:rgba(22,163,74,0.92);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-weight:900;font-size:18px;color:#fff;font-family:'Helvetica Neue',Arial,sans-serif;line-height:1;">$</div>`,
    className: '',
    iconSize: L.point(36, 36),
    iconAnchor: L.point(18, 18),
  }), []);

  const recurringTemplateMarkerIcon = useMemo(() => L.divIcon({
    html: `<div style="width:34px;height:34px;border-radius:50%;background:rgba(147,51,234,0.9);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-size:17px;color:#fff;line-height:1;">🔁</div>`,
    className: '',
    iconSize: L.point(34, 34),
    iconAnchor: L.point(17, 17),
  }), []);

  // Stable array reference for MarkerClusterGroup's children, so unrelated
  // re-renders of EventsMap don't hand it "new" markers (see
  // clusterIconCreateFunction above for the same reasoning).
  const markerElements = useMemo(
    () =>
      groupedEvents.map(({ locationKey, sorted }) => {
        const handlers = markerEventHandlers.find(m => m.locationKey === locationKey)?.handlers ?? { click: trackMarkerClick };
        return (
        <Marker
          key={locationKey}
          position={[sorted[0].lat, sorted[0].lng]}
          eventHandlers={handlers}
          icon={getCategoryIcon(getEventCategory(sorted[0]))}
        >
          <Popup closeButton={true} maxWidth={560} autoPan={false}>
            <PaginatedEventPopup
              events={sorted}
              isLoggedIn={isLoggedIn}
              isModOrAdmin={isModOrAdmin}
              onSave={handleEditSubmit}
              onDelete={handleDelete}
              onOpenImagePreview={openImagePreview}
              onReportCountChange={handleReportCountChange}
            />
          </Popup>
        </Marker>
        );
      }),
    [
      groupedEvents,
      markerEventHandlers,
      isModOrAdmin,
      getCategoryIcon,
      isLoggedIn,
      handleEditSubmit,
      handleDelete,
      openImagePreview,
      handleReportCountChange,
    ]
  );

  useEffect(() => {
    fetchEvents(startDate, endDate);
  }, [showArchived, startDate, endDate, bbox, fetchEvents]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (imagePreview) setImagePreview(null);
      else if (isContactModalOpen) setIsContactModalOpen(false);
      else if (isPrivacyPolicyModalOpen) setIsPrivacyPolicyModalOpen(false);
      else if (isTermsModalOpen) setIsTermsModalOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [imagePreview, isContactModalOpen, isPrivacyPolicyModalOpen, isTermsModalOpen]);

  if (isEventsLoading && !hasLoadedOnce) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  const sidebarFilters = (
    <>
      <DateFilter 
        onDateChange={handleDateFilterChange}
        startDate={startDate}
        endDate={endDate}
        onClearFilters={handleClearFilters}
        tagInput={tagInput}
        onTagInputChange={setTagInput}
        onTagInputSubmit={handleTagInputSubmit}
        selectedTags={selectedTags}
        availableTags={availableTags}
        onAddTag={(tag) => {
          const next = [...selectedTags, tag];
          setSelectedTags(next);
          fetchEvents(startDate, endDate, next);
        }}
        onRemoveTag={(tag) => {
          const next = selectedTags.filter(t => t !== tag);
          setSelectedTags(next);
          fetchEvents(startDate, endDate, next);
        }}
        isSearching={isSearching}
        searchResultCount={searchResults !== null ? searchResults.length : null}
      />
      <label id="show-archived-label" className="hidden">
        <input
          id="show-archived-checkbox"
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
          className="h-4 w-4 rounded border-orange-300 text-orange-600 focus:ring-orange-500"
        />
        <span
          id="show-archived-label"
          className="text-sm !text-white ![color:#ffffff]"
        >
          Pokaż tylko archiwalne wydarzenia
        </span>
      </label>
    </>
  );

  const floatingMenuButton = (
    <button
      id="floating-menu-button"
      onClick={handleSidebarToggle}
      className="fixed z-[2147483647] transform-gpu origin-top-left scale-50 md:scale-100"
      style={{
        top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
        left: '16px',
        width: '68px',
        height: '56px',
        borderRadius: '14px',
        backgroundColor: '#ea580c',
        border: '2px solid #ffffff',
        boxShadow: '0 14px 30px rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
      aria-label={isSidebarOpen ? 'Zamknij menu' : 'Otwórz menu'}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'relative',
          width: '30px',
          height: '24px',
          display: 'inline-block'
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: '2px',
            width: '30px',
            height: '4px',
            borderRadius: '999px',
            backgroundColor: '#ffffff',
            transformOrigin: 'center',
            transform: isSidebarOpen ? 'translateY(8px) rotate(45deg)' : 'translateY(0) rotate(0deg)',
            transition: 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        />
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: '10px',
            width: '30px',
            height: '4px',
            borderRadius: '999px',
            backgroundColor: '#ffffff',
            opacity: isSidebarOpen ? 0 : 1,
            transform: isSidebarOpen ? 'scaleX(0.4)' : 'scaleX(1)',
            transition: 'opacity 140ms ease-out, transform 200ms cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: '18px',
            width: '30px',
            height: '4px',
            borderRadius: '999px',
            backgroundColor: '#ffffff',
            transformOrigin: 'center',
            transform: isSidebarOpen ? 'translateY(-8px) rotate(-45deg)' : 'translateY(0) rotate(0deg)',
            transition: 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        />
      </span>
    </button>
  );

  const floatingSocialLinks = (
    <div
      id="floating-social-links"
      className="z-[1000] flex gap-2"
      style={{ pointerEvents: 'auto' }}
    >
      {/* Instagram */}
      <a
        href="https://www.instagram.com/kiedycogdzie.pl/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Instagram kiedycogdzie.pl"
        onClick={trackInstagramClick}
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '14px',
          background: 'rgba(22, 26, 45, 0.75)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.18)',
          boxShadow: '0 8px 24px rgba(5,8,20,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <rect x="2" y="2" width="20" height="20" rx="5" stroke="white" strokeWidth="1.8"/>
          <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.8"/>
          <circle cx="17.5" cy="6.5" r="1" fill="white"/>
        </svg>
      </a>

      {/* Discord */}
      <a
        href="#"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Discord kiedycogdzie.pl (wkrótce)"
        title="Discord — wkrótce"
        onClick={trackDiscordClick}
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '14px',
          background: 'rgba(22, 26, 45, 0.75)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.18)',
          boxShadow: '0 8px 24px rgba(5,8,20,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.6,
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.033.055a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" fill="white"/>
        </svg>
      </a>
    </div>
  );

  const floatingLegendAndSocialLinks = (
    <>
    <style>{`
      #floating-legend-and-social-links {
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        gap: 0.75rem;
      }
      @media (min-width: 768px) {
        #floating-legend-and-social-links {
          flex-direction: column;
          align-items: flex-end;
        }
        #map-zoom-tier-indicator-mobile {
          display: none;
        }
      }
      @media (max-width: 767px) {
        /* map-zoom-tier-indicator removed */
      }
    `}</style>
    <div
      id="floating-legend-and-social-links"
      style={{
        position: 'fixed',
        right: '1rem',
        bottom: '1.5rem',
        zIndex: 2147483646,
        pointerEvents: 'none',
      }}
    >
      {floatingSocialLinks}
      {!isSubmissionsView && !isRecurringTemplatesView && (
        <div id="floating-legend-container" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end', pointerEvents: 'auto' }}>
          <div id="map-zoom-tier-indicator" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
            <div
              style={{ borderRadius: '0.5rem', background: 'rgba(255,255,255,0.95)', padding: '0.375rem 0.75rem', fontSize: '0.875rem', fontWeight: 600, color: 'rgb(194 65 12)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
              title="Widok mapy dobierany automatycznie na podstawie przybliżenia"
            >
              {zoomTier === 'far' && '⬡ Heksagony'}
              {zoomTier === 'medium' && '⬡ Heksagony'}
              {zoomTier === 'near' && '⬡ Heksagony + markery'}
              {zoomTier === 'close' && '📍 Znaczniki'}
            </div>
            <button
              onClick={nearby ? handleClearNearby : handleNearMe}
              disabled={isLocating}
              style={{ borderRadius: '0.5rem', background: 'rgba(255,255,255,0.95)', padding: '0.375rem 0.75rem', fontSize: '0.875rem', fontWeight: 600, color: 'rgb(29 78 216)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', border: 'none', cursor: 'pointer', opacity: isLocating ? 0.6 : 1 }}
              title="Wydarzenia w pobliżu"
            >
              {isLocating ? '⏳ Szukam…' : nearby ? '✕ Wyczyść' : '📡 W pobliżu'}
            </button>
          </div>
          <div
            id="legend"
          className="text-xs"
          style={{
            maxWidth: 230,
            padding: '1rem',
            background: 'rgba(22, 26, 45, 0.5)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            boxShadow: '14px 0 36px rgba(5, 8, 20, 0.45)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '1rem',
            color: 'rgb(255, 255, 255)',
          }}
        >
          <div className="text-[10px] font-semibold uppercase tracking-wide mb-1.5">
            Liczba wydarzeń
          </div>
          <div className="flex items-center gap-2 mb-3">
            <span className="font-medium">1</span>
            <div
              style={{
                flex: 1,
                height: 6,
                borderRadius: 999,
                background: 'linear-gradient(to right, #86efac, #fde047, #fb923c, #ef4444, #db2777)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}
            />
            <span className="font-medium">200+</span>
          </div>
          <div id="divider-legend" className="hidden sm:block h-px bg-white/10 mb-2" />
          <div id="category-legend" className="hidden sm:grid grid-cols-2 gap-x-2 gap-y-1.5">
            {CATEGORY_ORDER.map(id => (
              <div key={id} className="flex items-center gap-1.5">
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: CATEGORIES[id].color,
                    border: '2px solid rgba(255,255,255,0.85)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.45)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    flexShrink: 0,
                  }}
                >
                  {CATEGORIES[id].icon}
                </span>
                <span className="text-[11px] leading-tight">{CATEGORIES[id].label}</span>
              </div>
            ))}
          </div>
        </div>
        </div>
      )}
    </div>
    </>
  );

  return (
    <div className="h-screen w-full flex overflow-hidden">
      {floatingLegendAndSocialLinks}

      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={handleSidebarClose}
        isLoggedIn={isLoggedIn}
        onLogout={handleLogout}
        onLoginClick={() => setIsLoginModalOpen(true)}
        onContactClick={openContactModal}
        isAdmin={userRole === 'ADMIN'}
        isLoading={isRoleLoading}
        isModerator={userRole === 'MODERATOR'}
      />
      {/* Filters top bar */}
      <div
        id="filters-top-bar"
        className="max-w-[min(90vw,600px)] md:max-w-[min(94vw,1000px)]"
        style={{
          position: 'fixed',
          top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 699,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          pointerEvents: 'auto',
          width: '100%',
          background: 'rgba(22, 26, 45, 0.85)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '14px 0 36px rgba(5, 8, 20, 0.45)',
          borderRadius: '1rem',
          padding: '1rem',
          color: '#e2e8f0',
        }}
      >
        {sidebarFilters}
      </div>
      {floatingMenuButton}

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Map Container */}
        <div className="flex-1 relative h-full">
          <MapContainer 
            center={[52.1, 19.4]} 
            zoom={7} 
            className="h-full w-full absolute inset-0"
            zoomControl={false} // Wyłączamy domyślną kontrolkę
          >
            <CenterPopupOnOpen />
            <PopupZIndexManager />
            <ZoomTierWatcher onZoomChange={handleZoomChange} />
            <MapBoundsWatcher onBoundsChange={handleBoundsChange} />
            <FlyToSearchResults events={searchResults} />
            {deepLinkEvent && (
              <DeepLinkMarker
                event={deepLinkEvent}
                onClose={handleDeepLinkHandled}
                isLoggedIn={isLoggedIn}
                isModOrAdmin={isModOrAdmin}
                onSave={handleEditSubmit}
                onDelete={handleDelete}
                onOpenImagePreview={openImagePreview}
                onReportCountChange={handleReportCountChange}
              />
            )}
            <ZoomControl position="topright" /> {/* Dodajemy własną kontrolkę w prawym górnym rogu */}
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution=""
            />
            {nearby && (
              <>
                <FitToRadius center={nearby.center} radius={nearby.radius} />
                <Circle
                  center={nearby.center}
                  radius={nearby.radius}
                  pathOptions={{ color: '#2563eb', weight: 2, fillColor: '#3b82f6', fillOpacity: 0.08 }}
                />
              </>
            )}
            {!isSubmissionsView && !isRecurringTemplatesView && zoomTier !== 'close' && !isSparsePhraseSearch && (
              <HexbinLayer
                events={hexEvents}
                markerThreshold={zoomTier === 'near' ? 5 : 0}
                onSparseEventsChange={handleSparseEventsChange}
                startDate={effectiveDateRange.startDate}
                endDate={effectiveDateRange.endDate}
              />
            )}
            {!isSubmissionsView && !isRecurringTemplatesView && (isSparsePhraseSearch || (zoomTier !== 'far' && zoomTier !== 'medium')) && <MarkerClusterGroup
              key={zoomTier}
              chunkedLoading
              // At the 'near' tier, hexagons are still on screen, so a small
              // cluster radius keeps sparse markers from being glommed into
              // a bubble that visually overlaps/obscures a hexagon.
              maxClusterRadius={zoomTier === 'near' ? 25 : 80}
              iconCreateFunction={clusterIconCreateFunction}
            >
              {markerElements}
            </MarkerClusterGroup>}

            {/* Recurring template markers (purple) — only visible in the dedicated cycles view */}
            {isRecurringTemplatesView && isModOrAdmin && recurringTemplates.map((template) => (
              <Marker key={template.id} position={[template.lat, template.lng]} icon={recurringTemplateMarkerIcon}>
                <Popup closeButton={true} maxWidth={480} autoPan={false}>
                  <RecurringTemplatePopup
                    template={template}
                    onSave={handleSaveRecurringTemplate}
                    onDelete={handleDeleteRecurringTemplate}
                    onOpenImagePreview={openImagePreview}
                  />
                </Popup>
              </Marker>
            ))}

            {/* Submissions markers (orange) — only visible in submissions view */}
            {isSubmissionsView && isModOrAdmin && submissions.map(sub => (
              <Marker
                key={sub.id}
                position={[sub.lat, sub.lng]}
                icon={sub.submissionId ? paidSubmissionMarkerIcon : submissionMarkerIcon}
              >
                <Popup closeButton={true} maxWidth={380} autoPan={false}>
                  <SubmittedEventPopup
                    event={sub}
                    onApprove={handleApproveAndRefresh}
                    onReject={handleRejectSubmission}
                    onDelete={handleDeleteSubmission}
                    onEdit={handleEditSubmission}
                    onOpenImagePreview={openImagePreview}
                  />
                </Popup>
              </Marker>
            ))}
            {/* Reported event markers (amber "?") — confirmed events with reports, also shown in submissions view */}
            {isSubmissionsView && isModOrAdmin && reportedEvents.map(event => (
              <Marker
                key={event.id}
                position={[event.lat, event.lng]}
                icon={reportedMarkerIcon}
              >
                <Popup closeButton={true} maxWidth={560} autoPan={false}>
                  <PaginatedEventPopup
                    events={[event]}
                    isLoggedIn={isLoggedIn}
                    isModOrAdmin={isModOrAdmin}
                    onSave={handleEditSubmit}
                    onDelete={handleDelete}
                    onOpenImagePreview={openImagePreview}
                    onReportCountChange={handleReportCountChange}
                  />
                </Popup>
              </Marker>
            ))}
            {filteredEvents.length === 0 && !isSubmissionsView && !isRecurringTemplatesView && (
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 glass-panel p-4 z-[1000]">
                <p className="text-gray-600">{nearby ? 'Brak wydarzeń w pobliżu' : 'Brak wydarzeń do wyświetlenia'}</p>
              </div>
            )}
          </MapContainer>
        </div>
      </div>

      {/* Submissions view top banner */}
      {isSubmissionsView && isModOrAdmin && (
        <>
        <style>{`
          @media (max-width: 767px) {
            #submissions-view-top-banner {
              font-size: 10px !important;
            }
          }
        `}</style>
        <div id='submissions-view-top-banner' style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: 'rgba(234,88,12,0.95)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 14,
          gap: 16,
        }}>
          <span>Tryb zgłoszeń — {submissions.length} zgłoszeń ({submissions.filter(s => s.status === 'PENDING').length} oczekujących), {reportedEvents.length} zgłoszonych nieprawidłowości</span>
          <button
            onClick={() => setIsSubmissionsView(false)}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
          >
            Wróć do mapy
          </button>
        </div>
        </>
      )}

      {/* Recurring templates view top banner */}
      {isRecurringTemplatesView && isModOrAdmin && (
        <>
        <style>{`
          @media (max-width: 767px) {
            #recurring-templates-view-top-banner {
              font-size: 10px !important;
            }
          }
        `}</style>
        <div id='recurring-templates-view-top-banner' style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: 'rgba(147,51,234,0.95)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 14,
          gap: 16,
        }}>
          <span>Tryb cykli wydarzeń — {recurringTemplates.length} cykli</span>
          <button
            onClick={() => setIsRecurringTemplatesView(false)}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
          >
            Wróć do mapy
          </button>
        </div>
        </>
      )}

      {isAddModalOpen && (
        <AddEventModal
          onSubmit={handleAddEvent}
          isLoggedIn={isLoggedIn}
          onCancel={() => {
            if (Date.now() - addModalOpenedAtRef.current < 260) {
              return;
            }
            setIsAddModalOpen(false);
          }}
        />
      )}

      {isAddUserModalOpen && (
        <AddUserModal
          onSubmit={() => {
            setIsAddUserModalOpen(false);
            alert('Użytkownik został dodany pomyślnie');
          }}
          onCancel={() => setIsAddUserModalOpen(false)}
        />
      )}



      {isLoginModalOpen && (
        <LoginModal
          onClose={() => setIsLoginModalOpen(false)}
          onSuccess={() => {
            setIsLoginModalOpen(false);
            setIsLoggedIn(true);
            window.location.reload();
          }}
        />
      )}

      {isContactModalOpen && (
        <ContactModal
          onClose={() => {
            if (Date.now() - contactModalOpenedAtRef.current < 260) {
              return;
            }
            setIsContactModalOpen(false);
          }}
        />
      )}

      {isPrivacyPolicyModalOpen && (
        <PrivacyPolicyModal
          onClose={() => {
            if (Date.now() - privacyPolicyModalOpenedAtRef.current < 260) {
              return;
            }
            setIsPrivacyPolicyModalOpen(false);
          }}
        />
      )}

      {isTermsModalOpen && (
        <TermsModal
          onClose={() => {
            if (Date.now() - termsModalOpenedAtRef.current < 260) {
              return;
            }
            setIsTermsModalOpen(false);
          }}
        />
      )}

      {imagePreview && createPortal(
        <div
          id="magnifying-glass"
          className="grid place-items-center p-4"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483647,
            background: 'rgba(0, 0, 0, 0.86)',
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Podgląd zdjęcia wydarzenia"
          onClick={() => {
            // Ignore the first backdrop click that can come from the same interaction used to open preview.
            if (Date.now() - imagePreviewOpenedAtRef.current < 260) {
              return;
            }
            setImagePreview(null);
          }}
        >
          <div
            id="magnifying-glass-panel"
            className="relative max-h-[92vh] max-w-[94vw]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              id="magnifying-glass-close"
              type="button"
              onClick={() => setImagePreview(null)}
              aria-label="Zamknij podgląd zdjęcia"
            >
              <svg
                id="magnifying-glass-close-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M6 6L18 18M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2.25"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <img
              id="magnifying-glass-image"
              src={imagePreview.imageUrl}
              alt={`${imagePreview.title} - podgląd`}
              className="max-h-[86vh] max-w-[94vw] rounded-xl object-contain"
            />
            <p id="magnifying-glass-title" className="mt-3 text-center text-sm text-slate-200">
              {imagePreview.title}
            </p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
